import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import PageSEO from '@/components/base/PageSEO';
import HeroBanner from './components/HeroBanner';
import CategoryQuickLinks from './components/CategoryQuickLinks';
import MovieSection from './components/MovieSection';
import { movies, tvShows, varietyShows } from '@/mocks/content';

const heroSlides = [movies[0], tvShows[0], varietyShows[2], movies[8]];

const hotMovies = movies.filter((m) => m.isHot);
const newContent = [...movies, ...tvShows, ...varietyShows]
  .filter((m) => m.isNew)
  .slice(0, 10);

const homeJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: '色影视',
  url: 'https://www.seyingshi.com/',
  description: '高清免费电影、高清免费电视剧、高清免费综艺大全，超清影视免费在线观看，不缓冲流畅播放，无需付费注册，最新热门影视剧集每日更新。',
  inLanguage: 'zh-CN',
  potentialAction: {
    '@type': 'SearchAction',
    target: 'https://www.seyingshi.com/search?q={search_term_string}',
    'query-input': 'required name=search_term_string',
  },
};

const orgJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '色影视',
  url: 'https://www.seyingshi.com/',
  description: '高清免费影视在线观看平台',
};

export default function Home() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  return (
    <>
      <PageSEO
        title="色影视 - 高清免费电影与高清免费电视剧全集 | 超清影视免费不缓冲在线看"
        description="高清免费电影、高清免费电视剧、高清免费综艺大全，超清影视免费在线观看，不缓冲流畅播放，无需付费注册，最新热门影视剧集每日更新。"
        keywords="高清免费电影,高清免费电视剧,高清免费综艺,超清影视免费,免费高清不缓冲,电视剧全集在线,电影完整版,色影视,影视在线观看,电影大全"
        canonicalUrl="https://www.seyingshi.com/"
        jsonLd={[homeJsonLd, orgJsonLd]}
      />
      <ScrollToTop />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          <HeroBanner slides={heroSlides} />

          <CategoryQuickLinks />

          <MovieSection
            title="正在热播"
            subtitle="大家都在看的热门影视"
            movies={hotMovies}
          />

          {/* Movie section */}
          <div className="w-full bg-background-100 py-2">
            <MovieSection
              title="热门电影"
              subtitle="院线大片 · 高分佳作"
              linkTo="/category/movie"
              movies={movies.slice(0, 10)}
            />
          </div>

          {/* TV section */}
          <MovieSection
            title="热门电视剧"
            subtitle="追剧必备 · 每日更新"
            linkTo="/category/tv"
            movies={tvShows.slice(0, 10)}
          />

          {/* Variety section */}
          <div className="w-full bg-background-100 py-2">
            <MovieSection
              title="热门综艺"
              subtitle="欢乐不断 · 精彩不停"
              linkTo="/category/variety"
              movies={varietyShows.slice(0, 8)}
            />
          </div>

          {/* Latest updates */}
          <MovieSection
            title="最新更新"
            subtitle="刚刚上架的精彩内容"
            movies={newContent}
          />

          {/* CTA */}
          <section className="w-full max-w-[1400px] mx-auto px-4 md:px-6 py-16">
            <div className="relative rounded-2xl overflow-hidden bg-background-950">
              <div className="absolute inset-0 opacity-20">
                <img
                  src="https://readdy.ai/api/search-image?query=cinematic%20movie%20theater%20atmosphere%20with%20dark%20red%20velvet%20curtains%20and%20warm%20spotlight%2C%20elegant%20classic%20cinema%20interior%2C%20dramatic%20lighting%20with%20deep%20shadows%2C%20luxurious%20and%20inviting%20movie%20watching%20environment%2C%20rich%20warm%20tones%2C%20professional%20cinematography%20style&width=1400&height=400&seq=cta-bg-home&orientation=landscape"
                  alt=""
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="relative z-10 px-6 py-12 md:py-16 text-center">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-3">
                  随时随地，免费畅享高清影视
                </h2>
                <p className="text-foreground-300 text-sm md:text-base max-w-lg mx-auto mb-6">
                  无需注册，无需付费，打开就能看。海量高清电影、电视剧、综艺资源每日更新，给你最好的观影体验。
                </p>
                <div className="flex items-center justify-center gap-3">
                  {['movie', 'tv', 'variety'].map((cat) => (
                    <Link
                      key={cat}
                      to={`/category/${cat}`}
                      className="px-5 py-2.5 bg-background-50/10 hover:bg-background-50/20 text-background-50 text-sm font-medium rounded-lg border border-background-50/20 hover:border-background-50/40 transition-all duration-200 whitespace-nowrap cursor-pointer"
                    >
                      {cat === 'movie' ? '看电影' : cat === 'tv' ? '追剧集' : '刷综艺'}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}